import {
    createListenerMiddleware,
    isRejectedWithValue,
} from '@reduxjs/toolkit';

import {
    invalidateSession,
    isFetchBaseQueryError,
    isTokenValidationError,
} from '@/shared/api';

import { authFailurePopupActions } from '../slices/auth-failure-popup.slice';

export const authFailurePopupListener = createListenerMiddleware();

const startAuthFailurePopupListener =
    authFailurePopupListener.startListening as TypedListening;

// List of auth endpoint names that should not trigger the auth failure popup
const AUTH_ENDPOINT_NAMES = [
    'signIn',
    'signUp',
    'google',
    'confirmEmail',
    'initiatePasswordRecovery',
    'resetPassword',
    'checkResetPasswordParams',
];

/**
 * Check if the action type is from an authentication endpoint
 * @param actionType - The action type to check
 */
function isAuthEndpointAction(actionType: string): boolean {
    return AUTH_ENDPOINT_NAMES.some((endpointName) =>
        actionType.includes(endpointName),
    );
}

// Listen for invalidateSession action - but exclude voluntary logouts and auth endpoint failures
startAuthFailurePopupListener({
    actionCreator: invalidateSession,
    effect: (action, { dispatch }) => {
        const isVoluntaryLogout = action.payload?.isVoluntaryLogout ?? false;
        const isAuthEndpointFailure =
            action.payload?.isAuthEndpointFailure ?? false;

        // Don't show popup for voluntary logouts or auth endpoint failures (login, signup, etc.)
        if (!isVoluntaryLogout && !isAuthEndpointFailure) {
            dispatch(authFailurePopupActions.showPopup());
        }
    },
});

// Also listen for token validation errors that might not trigger invalidateSession
startAuthFailurePopupListener({
    matcher: isRejectedWithValue,
    effect: (action, { dispatch }) => {
        if (isFetchBaseQueryError(action.payload)) {
            const error = action.payload;

            // Don't show popup for auth endpoints (login, signup, etc.)
            // Check the action type to determine if it's from an auth endpoint
            if (isAuthEndpointAction(action.type)) {
                return;
            }

            // Check for token validation errors
            if (
                isTokenValidationError(error) &&
                (error.status === 401 || error.status === 403)
            ) {
                dispatch(authFailurePopupActions.showPopup());
            }
        }
    },
});
